import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  author?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  robots?: string;
}

const SEO = ({ 
  title, 
  description, 
  keywords, 
  ogImage, 
  canonical,
  author = 'NearSmarter',
  articlePublishedTime,
  articleModifiedTime,
  robots
}: SEOProps) => {
  const siteUrl = 'https://nearsmarter.com';
  const defaultOgImage = `${siteUrl}/logo_normal.webp`;
  const ogImageUrl = ogImage || defaultOgImage;
  const ogImageType = ogImage?.toLowerCase().endsWith('.webp') ? 'image/webp' : 'image/png';
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      {robots && <meta name="robots" content={robots} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NearSmarter" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      
      {/* Article specific meta tags */}
      {articlePublishedTime && (
        <>
          <meta property="article:published_time" content={articlePublishedTime} />
          <meta name="article:published_time" content={articlePublishedTime} />
        </>
      )}
      {articleModifiedTime && (
        <>
          <meta property="article:modified_time" content={articleModifiedTime} />
          <meta name="article:modified_time" content={articleModifiedTime} />
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};

export default SEO;
